import {
  clearChatHandoffStartingAt,
  STARTING_CHAT_HANDOFF_TTL_MS,
  storeChatHandoffStartingAt,
} from '../utils/chatHandoff'

const DEFAULT_COACH_AVATAR =
  'https://www-yns-com.s3.amazonaws.com/images/icon_coach_generic.png'

/**
 * RtcService handles EasyRTC connections, room management, and participant presence
 *
 * This service manages WebRTC connections using Open-EasyRTC, providing a high-level
 * API for connecting to the EasyRTC server, joining rooms, managing peer connections,
 * and handling media streams. It coordinates with CallService and MediaService to provide
 * complete chat functionality for both candidates and coaches.
 *
 * The service uses Open-EasyRTC (https://github.com/open-easyrtc/open-easyrtc) for WebRTC
 * functionality. For detailed API documentation, see:
 * - Client API: https://open-easyrtc.github.io/open-easyrtc/client/Easyrtc.html
 * - Client Tutorial: https://github.com/open-easyrtc/open-easyrtc/blob/develop/docs/easyrtc_client_tutorial.md
 * - Rooms: https://github.com/open-easyrtc/open-easyrtc/blob/develop/docs/easyrtc_rooms.md
 *
 * @class RtcService
 */
class RtcService {
  /**
   * Creates a new RtcService instance
   *
   * Initializes all internal state for managing EasyRTC connections, room occupants,
   * event listeners, and heartbeat monitoring. The service maintains connection state
   * and tracks both coaches and candidates in the chat room.
   */
  constructor() {
    /** @type {Object|null} The EasyRTC client instance */
    this.easyrtc = null

    /**
     * @type {Object} Current connection state
     * @property {boolean} isConnected - Whether connected to EasyRTC server
     * @property {Array<Object>} coaches - List of coaches in the room
     * @property {Array<Object>} candidates - List of candidates in the room
     * @property {string|null} chatRoom - Current chat room name
     * @property {string|null} myEasyrtcid - This user's EasyRTC ID
     * @property {string|null} coachEasyRtcId - Coach's EasyRTC ID (for candidate mode)
     * @property {string} userMode - User mode: 'coach', 'candidate', or 'coach-list'
     */
    this.connectionState = {
      isConnected: false,
      coaches: [],
      candidates: [],
      chatRoom: null,
      myEasyrtcid: null,
      coachEasyRtcId: null,
      userMode: 'coach', // Default to coach mode
    }

    /** @type {Map<string, Set<Function>>} Map of event names to their callback sets */
    this.listeners = new Map()

    /** @type {number|null} Timeout ID for debouncing room updates */
    this.roomUpdateTimeout = null

    /** @type {Promise|null} Promise for ongoing connection attempt */
    this._connectionPromise = null

    /** @type {number|null} Interval ID for sending heartbeat messages */
    this.heartbeatInterval = null

    /** @type {number|null} Interval ID for checking coach heartbeat */
    this.heartbeatCheckInterval = null

    /** @type {string|null} API path for logging chat events */
    this.logChatEventPath = null

    /** @type {number} Server time offset for synchronizing timestamps */
    this.serverTimeOffset = 0

    /** @type {Function|null} Visibility change handler */
    this._visibilityChangeHandler = null

    /** @type {Function|null} Before unload handler */
    this._beforeUnloadHandler = null

    /** @type {Function|null} Page hide handler */
    this._pageHideHandler = null

    /** @type {boolean} Whether we're currently reconnecting */
    this._isReconnecting = false

    /** @type {number|null} Timeout ID for reconnection delay */
    this._reconnectTimeout = null

    /** @type {Object|null} Params from the last successful connect(), used to rebuild the connection on reconnect */
    this._connectionParams = null

    /** @type {number} Consecutive failed reconnection attempts since the last successful connect */
    this._reconnectAttempts = 0

    /** @type {number} Max consecutive reconnection attempts before giving up */
    this._maxReconnectAttempts = 5

    /** @type {Date|null} Last time we detected visibility */
    this._lastVisibilityChange = null

    /** @type {boolean} Whether connection was lost due to visibility change */
    this._connectionLostDueToVisibility = false

    /** @type {Object|null} Last room occupants map (for handoff TTL reprocessing) */
    this._lastOccupants = null

    /** @type {number|null} Timeout to reprocess candidates when handoff TTL expires */
    this._handoffTtlTimeout = null

    // Set up visibility and unload handlers
    this._setupVisibilityHandlers()
    this._setupUnloadHandlers()
  }

  /**
   * Connects to the EasyRTC server and joins a chat room
   *
   * Establishes a connection to the EasyRTC server, configures the client, and joins
   * the specified chat room. If already connected or connecting, returns the existing
   * connection state or promise. This method is idempotent and safe to call multiple times.
   *
   * See EasyRTC connection documentation:
   * https://open-easyrtc.github.io/open-easyrtc/client/Easyrtc.html#connect
   *
   * @param {string} socketUrl - WebSocket URL of the EasyRTC server
   * @param {string} username - Username for this connection
   * @param {string} chatRoom - Name of the chat room to join
   * @param {Object} [options={}] - Connection options
   * @param {string} [options.appName='yns.coach_chat'] - EasyRTC application name
   * @param {boolean} [options.enableAudio=false] - Enable audio for this connection
   * @param {boolean} [options.enableVideo=false] - Enable video for this connection
   * @param {boolean} [options.videoEnabled=false] - Whether video is enabled
   * @param {string} [options.userMode='coach'] - User mode: 'coach', 'candidate', or 'coach-list'
   * @param {string} [options.coachEasyRtcId] - Coach's EasyRTC ID (for candidate mode)
   * @param {boolean} [options.debug=false] - Enable debug logging
   * @returns {Promise<Object>} Promise that resolves to the connection state
   */
  async connect(socketUrl, username, chatRoom, options = {}) {
    // If already connecting, return existing promise
    if (this._connectionPromise) {
      return this._connectionPromise
    }

    // If already connected, return current state — but rejoin if we left the
    // room (e.g. coach-list modal closed; CTS-9867).
    if (this.easyrtc && this.connectionState.isConnected) {
      if (chatRoom && this.connectionState.chatRoom !== chatRoom) {
        await this.joinRoom(chatRoom, options)
        this.notifyListeners('connected', this.connectionState)
      }
      return this.connectionState
    }

    // Create new connection promise
    this._connectionPromise = this._performConnection(
      socketUrl,
      username,
      chatRoom,
      options,
    )

    try {
      const result = await this._connectionPromise
      return result
    } finally {
      this._connectionPromise = null
    }
  }

  /**
   * Performs the actual EasyRTC connection
   *
   * Internal method that handles the connection logic. Waits for Open-EasyRTC to be
   * available, configures the EasyRTC client, sets up stream handlers, and connects
   * to the server. This is called by the public connect() method.
   *
   * @param {string} socketUrl - WebSocket URL of the EasyRTC server
   * @param {string} username - Username for this connection
   * @param {string} chatRoom - Name of the chat room to join
   * @param {Object} [options={}] - Connection options
   * @returns {Promise<Object>} Promise that resolves to the connection state
   * @private
   */
  async _performConnection(socketUrl, username, chatRoom, options = {}) {
    // Wait for Open-EasyRTC to be ready if it's not already
    if (!window.openEasyRtc && !window.openEasyRtcReady) {
      // console.log('RtcService - Waiting for Open-EasyRTC to be ready...')
      await this._waitForOpenEasyRTC()
    }

    // Store params so a lost connection can be rebuilt from scratch on reconnect
    // (see _handleVisibilityReconnection) instead of relying on socket.io's own
    // uncapped default reconnection, which would otherwise retry against the
    // signaling/TURN server indefinitely if the backend is unreachable.
    this._connectionParams = { socketUrl, username, chatRoom, options }

    this.easyrtc = window.openEasyRtc
    // Cap socket.io's built-in auto-reconnect so a dead signaling server gets a
    // bounded number of retries with backoff instead of being hammered forever.
    // Keys are duplicated in both the legacy (space-separated) and modern
    // socket.io option formats since we don't control which client version the
    // signaling server serves.
    // Match legacy chat.js: bare socket URL only — do not append ?session=.
    // A client-invented session query caused staging 400s ("session not valid").
    this.easyrtc.setSocketUrl(socketUrl, {
      'connect timeout': 10000,
      'force new connection': true,
      'reconnection limit': 5000,
      'max reconnection attempts': 10,
      'reconnection delay': 1000,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    })
    this.easyrtc.enableDebug(false)

    // Don't set specific video dimensions - let browser choose optimal resolution for device
    // This prevents constraint errors on mobile devices that don't support high resolutions
    // Removed: this.easyrtc.setVideoDims(1280, 720)

    // Suppress EasyRTC's default error dialog - only log to console
    // This prevents resolution constraint errors from appearing as visible UI messages
    this.easyrtc.setErrorListener(({ errorCode, errorText }) => {
      // Only log to console for debugging, don't show error dialog to users
      if (options.debug) {
        console.warn(`EasyRTC ${errorCode}:`, errorText)
      }
    })

    // Configure EasyRTC - IMPORTANT: These must be set BEFORE connecting
    this.easyrtc.enableAudio(options.enableAudio || false)
    this.easyrtc.enableVideo(options.enableVideo || false)
    this.easyrtc.enableDataChannels(true) // Enable data channels for messaging
    this.easyrtc.setUsername(username)

    // Set up stream acceptor for incoming media streams
    this.easyrtc.setStreamAcceptor((easyrtcid, stream, streamName) => {
      this._handleIncomingStream(easyrtcid, stream, streamName)
    })

    // Set up stream closed handler
    this.easyrtc.setOnStreamClosed((easyrtcid) => {
      this.notifyListeners('streamClosed', { easyrtcid })
    })

    // Set up disconnection handler for mobile visibility changes
    // Note: setDisconnectListener is called when the server disconnects us,
    // not when we intentionally disconnect
    this.easyrtc.setDisconnectListener(() => {
      // Only handle if we're not already disconnecting intentionally
      if (!this._isReconnecting && !this._connectionLostDueToVisibility) {
        this._connectionLostDueToVisibility = true
        this.connectionState.isConnected = false
        this.notifyListeners('disconnected', this.connectionState)
      }
    })

    // Store coachEasyRtcId in connection state for filtering room updates
    if (options.coachEasyRtcId) {
      this.connectionState.coachEasyRtcId = options.coachEasyRtcId
    }

    return new Promise((resolve, reject) => {
      const loginSuccess = (easyrtcid) => {
        this.joinRoom(chatRoom, options)
          .then(() => {
            this.connectionState.isConnected = true
            this.connectionState.myEasyrtcid = easyrtcid
            this.connectionState.username = username
            // Set videoEnabled property from options
            this.connectionState.videoEnabled = options.videoEnabled || false
            this._reconnectAttempts = 0
            this.notifyListeners('connected', this.connectionState)
            resolve(this.connectionState)
            return this.connectionState
          })
          .catch(reject)
      }

      const loginFailure = (_errorCode, errorText) => {
        reject(new Error(`Connection failed: ${errorText}`))
      }

      this.easyrtc.connect(
        options.appName || 'yns.coach_chat',
        loginSuccess,
        loginFailure,
      )
    })
  }

  /**
   * Handles incoming media stream from peer
   *
   * Called by EasyRTC when a remote media stream is received. Sets up a video element
   * for playback (required for audio streams in EasyRTC) and notifies listeners.
   * Handles browser autoplay policies with retry logic.
   *
   * See EasyRTC stream handling:
   * https://open-easyrtc.github.io/open-easyrtc/client/Easyrtc.html#setStreamAcceptor
   *
   * @param {string} easyrtcid - EasyRTC ID of the peer sending the stream
   * @param {MediaStream} stream - The incoming media stream
   * @param {string} streamName - Name of the stream
   * @private
   */
  _handleIncomingStream(easyrtcid, stream, streamName) {
    this.remoteStream = stream

    // Set up the stream for audio/video playback using EasyRTC's method
    // This is crucial for audio streams to work properly - the legacy implementation
    // uses easyrtc.setVideoObjectSrc() which handles both video and audio streams
    if (stream) {
      // Create a hidden video element to handle the stream (EasyRTC's approach)
      // This is necessary because EasyRTC's setVideoObjectSrc handles both video and audio
      let videoElement = document.getElementById('callerVideo')
      if (!videoElement) {
        videoElement = document.createElement('video')
        videoElement.id = 'callerVideo'
        videoElement.style.display = 'none'
        // DO NOT mute the remote stream - this prevents audio playback
        // Only local streams (selfVideo) should be muted to prevent echo
        videoElement.muted = false
        // Set volume to maximum to ensure audio is audible
        videoElement.volume = 1.0
        // Add autoplay and playsinline attributes to help with browser autoplay policies
        videoElement.autoplay = true
        videoElement.playsInline = true
        document.body.appendChild(videoElement)
      } else {
        // If element already exists, ensure it's not muted and volume is set
        videoElement.muted = false
        videoElement.volume = 1.0
      }

      // Use EasyRTC's method to properly set up the stream for playback
      this.easyrtc.setVideoObjectSrc(videoElement, stream)

      // Ensure the video element plays to activate audio
      // Try multiple times with a small delay to work around autoplay restrictions
      const attemptPlay = (retries = 3) => {
        return videoElement
          .play()
          .then(() => {
            // console.log('RtcService - Remote audio stream playing successfully')
            return true
          })
          .catch((error) => {
            console.warn(
              'RtcService - Error playing remote audio stream:',
              error,
            )
            if (retries > 0) {
              // console.log(
              //   `RtcService - Retrying audio playback (${retries} attempts remaining)`,
              // )
              setTimeout(() => attemptPlay(retries - 1), 500)
              return false
            } else {
              console.error(
                'RtcService - Failed to play remote audio after multiple attempts. This may be due to browser autoplay policies. User interaction may be required.',
              )
              // Notify listeners about the autoplay failure
              this.notifyListeners('autoplayBlocked', {
                easyrtcid,
                stream,
                element: videoElement,
              })
              return false
            }
          })
      }

      attemptPlay()
    }

    // Notify listeners about the incoming stream - let React components handle UI updates
    this.notifyListeners('remoteStreamReceived', {
      easyrtcid,
      stream,
      streamName,
      hasVideo: stream.getVideoTracks().length > 0,
      hasAudio: stream.getAudioTracks().length > 0,
    })
  }

  /**
   * Joins an EasyRTC room
   *
   * Joins the specified chat room and sets up room callbacks. After joining,
   * updates the coaches list and sets room fields based on user mode.
   *
   * See EasyRTC rooms documentation:
   * https://open-easyrtc.github.io/open-easyrtc/client/Easyrtc.html#joinRoom
   * https://github.com/open-easyrtc/open-easyrtc/blob/develop/docs/easyrtc_rooms.md
   *
   * @param {string} chatRoom - Name of the room to join
   * @param {Object} [options={}] - Options for room setup
   * @returns {Promise<string>} Promise that resolves to the room name
   */
  async joinRoom(chatRoom, options = {}) {
    return new Promise((resolve, reject) => {
      this.easyrtc.joinRoom(
        chatRoom,
        {},
        (roomName) => {
          this.connectionState.chatRoom = roomName
          this.setupRoomCallbacks()
          this.setRoomFields(options)

          // Get initial coaches list after joining room
          this.updateCoachesFromRoom()

          resolve(roomName)
        },
        (_errorCode, errorText) => {
          reject(new Error(`Failed to join room: ${errorText}`))
        },
      )
    })
  }

  /**
   * Sets up EasyRTC room callbacks
   *
   * Configures all event handlers for room events including call acceptance/rejection,
   * peer messages, typing indicators, and room occupant updates. This must be called
   * after joining a room.
   *
   * @private
   */
  setupRoomCallbacks() {
    // console.log('RtcService - Setting up room callbacks...')

    // Set up EasyRTC callbacks
    // console.log('RtcService - Setting up setCallCancelled...')
    this.easyrtc.setCallCancelled((easyrtcid, explicitlyCancelled) => {
      this.notifyListeners('callCancelled', { easyrtcid, explicitlyCancelled })
    })
    // console.log('RtcService - setCallCancelled set up successfully')

    // Set up call acceptance checking for incoming calls
    this.setupCallAcceptance()

    this.easyrtc.setPeerListener((who, msgType, content, _column) => {
      // Handle different message types
      if (msgType === 'call_accepted') {
        // console.log('RtcService - Call accepted by coach:', who)
        this.notifyListeners('callAccepted', {
          fromEasyrtcid: who,
          messageData: content,
        })
        return
      }

      if (msgType === 'call_rejected') {
        // console.log('RtcService - Call rejected by coach:', who)
        this.notifyListeners('callRejected', {
          fromEasyrtcid: who,
          messageData: content,
        })
        return
      }

      if (msgType === 'call_missed') {
        this.notifyListeners('callMissed', {
          fromEasyrtcid: who,
          messageData: content,
        })
        return
      }

      // Handle call cancelled by candidate (when candidate closes before coach answers)
      if (msgType === 'call_cancelled') {
        this.notifyListeners('callCancelled', {
          fromEasyrtcid: who,
          messageData: content,
        })
        return
      }

      // Handle chat ended by coach
      if (msgType === 'chat_ended') {
        // console.log('RtcService - Chat ended by coach:', who)

        // Update candidate presence when chat ended
        this.updateCandidatePresenceAfterChatEnd()

        this.notifyListeners('chatEndedByCoach', {
          fromEasyrtcid: who,
          messageData: content,
        })
        return
      }

      // Handle typing indicators
      if (msgType === 'typing') {
        // Emit appropriate event based on user mode
        if (this.connectionState.userMode === 'coach') {
          // Coach is receiving typing from candidate
          this.notifyListeners('candidateTyping', {
            fromEasyrtcid: who,
            messageData: content,
          })
        } else {
          // Candidate is receiving typing from coach
          this.notifyListeners('coachTyping', {
            fromEasyrtcid: who,
            messageData: content,
          })
        }
        return
      }

      // Handle call_start messages - this triggers the incoming call flow for coaches
      if (msgType === 'call_start') {
        // console.log('[RtcService] Received call_start message:', {
        //   fromEasyrtcid: who,
        //   content,
        //   userMode: this.connectionState.userMode,
        //   timestamp: new Date().toISOString(),
        // })

        // For coaches, this should trigger the incoming call notification
        if (this.connectionState.userMode === 'coach') {
          // Check candidate's presence to see if they have "Chat Ended" status
          const roomOccupants = this.easyrtc.getRoomOccupantsAsMap(
            this.connectionState.chatRoom,
          )
          const candidateOccupant = roomOccupants?.[who]
          const candidatePresence = candidateOccupant?.presence

          // console.log('[RtcService] call_start - Candidate presence check:', {
          //   candidateEasyrtcid: who,
          //   presence: candidatePresence,
          //   presenceStatus: candidatePresence?.status,
          //   isChatEnded: candidatePresence?.status === 'Chat Ended',
          // })

          // If candidate has "Chat Ended" presence, check if this is a reconnection
          // Allow reconnection if conversationId matches an existing active chat
          if (candidatePresence?.status === 'Chat Ended') {
            // Check if this conversationId matches an existing active chat (reconnection case)
            const isReconnection =
              conversationId &&
              this.connectionState.activeChats?.some(
                (chat) => chat.conversationId === conversationId,
              )

            if (!isReconnection) {
              console.warn(
                '[RtcService] call_start - Ignoring call_start from candidate with Chat Ended presence (not a reconnection):',
                {
                  candidateEasyrtcid: who,
                  conversationId: conversationId,
                  presence: candidatePresence,
                },
              )
              return
            } else {
              // This is a reconnection - allow it to proceed
            }
          }

          // Get candidate info from the room
          const chatMode = this.easyrtc.getRoomApiField(
            this.connectionState.chatRoom,
            who,
            'chatMode',
          )
          const conversationId = this.easyrtc.getRoomApiField(
            this.connectionState.chatRoom,
            who,
            'conversationId',
          )

          // Create a mock accept/reject callback since this isn't a real EasyRTC call
          const mockCallback = (accepted) => {
            /*
            console.log(
              'RtcService - Mock callback called with accepted:',
              accepted,
            )
            */
            // Send response back to candidate
            if (accepted) {
              this.easyrtc.sendData(
                who,
                'call_accepted',
                this.connectionState.myEasyrtcid,
              )
            } else {
              this.easyrtc.sendData(
                who,
                'call_rejected',
                this.connectionState.myEasyrtcid,
              )
            }
          }

          // Fire the incomingCall event
          // Include conversationId so coach can match reconnections
          this.notifyListeners('incomingCall', {
            fromEasyrtcid: who,
            acceptCallback: () => mockCallback(true),
            rejectCallback: () => mockCallback(false),
            chatMode: chatMode,
            conversationId: conversationId,
            candidateEasyrtcid: who, // Also include as candidateEasyrtcid for compatibility
          })

          return
        }
      }

      // Handle call_accepted messages - this should indicate the candidate received acceptance
      if (msgType === 'call_accepted') {
        /*
        console.log(
          'RtcService - Received call_accepted message from:',
          who,
          'content:',
          content,
        )
        */
        // This should trigger the candidate to initiate the actual EasyRTC call
      }

      // Handle call_rejected messages
      if (msgType === 'call_rejected') {
        /*
        console.log(
          'RtcService - Received call_rejected message from:',
          who,
          'content:',
          content,
        )
        */
      }

      // Handle video_failed messages - when candidate's media fails
      if (msgType === 'video_failed') {
        // Notify listeners about media failure from the other party
        this.notifyListeners('videoFailed', {
          fromEasyrtcid: who,
          messageData: content,
          errorData: content, // The error data from the media failure
        })
        return
      }

      // Handle candidate_action messages (for coach mode)
      if (
        msgType === 'candidate_action' &&
        this.connectionState.userMode === 'coach'
      ) {
        // Normalize content to an object (EasyRTC may pass msgData as string in some paths)
        let contentObj = content
        if (typeof content === 'string') {
          try {
            contentObj = JSON.parse(content)
          } catch (_e) {
            contentObj = {}
          }
        } else if (content == null || typeof content !== 'object') {
          contentObj = {}
        }
        content = contentObj

        // Get sender's display name from room occupants
        let senderName = 'Unknown'
        if (this.connectionState.chatRoom) {
          const occupants = this.easyrtc.getRoomOccupantsAsMap(
            this.connectionState.chatRoom,
          )
          const occupant = occupants[who]
          if (occupant) {
            if (occupant.apiField?.displayName?.fieldValue) {
              senderName = decodeURIComponent(
                occupant.apiField.displayName.fieldValue,
              )
            } else if (occupant.apiField?.firstName?.fieldValue) {
              senderName = decodeURIComponent(
                occupant.apiField.firstName.fieldValue,
              )
            } else {
              senderName = occupant.username || 'Unknown'
            }
          }
        }

        // Helper to format action text
        const formatActionText = (actionType, actionData) => {
          if (actionType === 'chat_attachment') {
            const fileName = actionData.attachment_name || 'file'
            return `sent ${fileName}`
          }
          switch (actionType) {
            case 'navigate':
              return `navigated to ${
                actionData.action_target?.pathname ||
                actionData.action_target ||
                ''
              }`
            case 'started_chat_from':
              return `started chat from ${actionData.action_target || ''}`
            case 'left_chat':
              return 'Candidate has left the chat'
            case 'pause':
              return actionData.paused
                ? 'paused their camera'
                : 'unpaused their camera'
            case 'mute':
              return actionData.paused
                ? 'muted their audio'
                : 'unmuted their audio'
            default:
              return actionType || 'performed an action'
          }
        }

        const actionType = content?.action_type || 'unknown'

        // text_disabled/text_enabled are not chat messages - coach handles them to hide/show text panel only
        const isTextDisabledAction =
          actionType === 'text_disabled' || actionType === 'text_enabled'

        const message = isTextDisabledAction
          ? null
          : (() => {
              const actionText = formatActionText(actionType, content)
              return {
                id: Date.now(),
                type: 'candidate_action',
                actionType: actionType,
                actionData: content,
                text: actionText,
                timestamp: new Date().toISOString(),
                sender: 'candidate',
                senderName: senderName,
                fromEasyrtcid: who,
                ...(actionType === 'chat_attachment' && {
                  attachment: {
                    name: content.attachment_name || 'file',
                    url: content.action_target,
                    type: content.attachment_type || 'application/octet-stream',
                  },
                }),
              }
            })()

        // Notify listeners with the message data (message is null for text_disabled/text_enabled)
        this.notifyListeners('peerMessage', {
          messageType: msgType,
          messageData: content,
          fromEasyrtcid: who,
          message,
        })
        return
      }

      // Handle coach_action messages (for candidate mode)
      if (
        msgType === 'coach_action' &&
        this.connectionState.userMode === 'candidate'
      ) {
        // Get sender's display name from room occupants
        let senderName = 'Unknown'
        if (this.connectionState.chatRoom) {
          const occupants = this.easyrtc.getRoomOccupantsAsMap(
            this.connectionState.chatRoom,
          )
          const occupant = occupants[who]
          if (occupant) {
            if (occupant.apiField?.displayName?.fieldValue) {
              senderName = decodeURIComponent(
                occupant.apiField.displayName.fieldValue,
              )
            } else if (occupant.apiField?.firstName?.fieldValue) {
              senderName = decodeURIComponent(
                occupant.apiField.firstName.fieldValue,
              )
            } else {
              senderName = occupant.username || 'Unknown'
            }
          }
        }

        // Helper to format action text
        const formatActionText = (actionType, actionData) => {
          if (actionType === 'chat_attachment') {
            const fileName = actionData.attachment_name || 'file'
            return `sent ${fileName}`
          }
          switch (actionType) {
            case 'navigate':
              return `navigated to ${
                actionData.action_target?.pathname ||
                actionData.action_target ||
                ''
              }`
            case 'started_chat_from':
              return `started chat from ${actionData.action_target || ''}`
            case 'left_chat':
              return 'Candidate has left the chat'
            case 'pause':
              return actionData.paused
                ? 'paused their camera'
                : 'unpaused their camera'
            case 'mute':
              return actionData.paused
                ? 'muted their audio'
                : 'unmuted their audio'
            case 'ended_chat':
              return 'ended the chat'
            default:
              return actionType || 'performed an action'
          }
        }

        const actionType = content?.action_type || 'unknown'
        const actionText = formatActionText(actionType, content)

        // Format coach_action message
        const message = {
          id: Date.now(),
          type: 'coach_action',
          actionType: actionType,
          actionData: content,
          text: actionText, // Add text field for chat history
          timestamp: new Date().toISOString(),
          sender: 'coach',
          senderName: senderName,
          fromEasyrtcid: who,
          // For chat_attachment, include attachment info
          ...(actionType === 'chat_attachment' && {
            attachment: {
              name: content.attachment_name || 'file',
              url: content.action_target,
              type: content.attachment_type || 'application/octet-stream',
              size: content.attachment_size,
              id: content.attachment_id,
            },
          }),
        }

        // Notify listeners with the message data
        this.notifyListeners('peerMessage', {
          messageType: msgType,
          messageData: content,
          fromEasyrtcid: who,
          message, // Include the formatted message
        })
        return
      }

      // For text messages, add them to the messages array
      if (msgType === 'text' || msgType === 'message') {
        // Determine sender based on user mode and who sent the message
        let sender = 'unknown'
        if (this.connectionState.userMode === 'coach') {
          // If I'm a coach, messages from others are from candidates
          sender =
            who === this.connectionState.myEasyrtcid ? 'coach' : 'candidate'
        } else {
          // If I'm a candidate, messages from others are from coaches
          sender =
            who === this.connectionState.myEasyrtcid ? 'candidate' : 'coach'
        }

        // Get sender's display name from room occupants
        let senderName = 'Unknown'
        if (this.connectionState.chatRoom) {
          const occupants = this.easyrtc.getRoomOccupantsAsMap(
            this.connectionState.chatRoom,
          )
          const occupant = occupants[who]
          if (occupant) {
            if (occupant.apiField?.displayName?.fieldValue) {
              senderName = decodeURIComponent(
                occupant.apiField.displayName.fieldValue,
              )
            } else if (occupant.apiField?.firstName?.fieldValue) {
              senderName = decodeURIComponent(
                occupant.apiField.firstName.fieldValue,
              )
            } else {
              senderName = occupant.username || 'Unknown'
            }
          }
        }

        const message = {
          id: Date.now(),
          type: 'text',
          content: content,
          timestamp: new Date().toISOString(),
          sender: sender,
          senderName: senderName,
          fromEasyrtcid: who,
        }

        // Notify listeners with the message data
        this.notifyListeners('peerMessage', {
          messageType: msgType,
          messageData: content,
          fromEasyrtcid: who,
          message, // Include the formatted message
        })
      } else {
        // For all other messages, pass them through
        this.notifyListeners('peerMessage', {
          messageType: msgType,
          messageData: content,
          fromEasyrtcid: who,
        })
      }
    })

    // Set up data channel open listener
    this.easyrtc.setDataChannelOpenListener((easyrtcid) => {
      // console.log('RtcService - Data channel opened with:', easyrtcid)

      // Notify listeners that data channel is open
      this.notifyListeners('dataChannelOpen', { easyrtcid })
    })

    // Set up room occupant listener for real-time presence updates
    this.easyrtc.setRoomOccupantListener((roomName, occupants, _myInfo) => {
      if (roomName === this.connectionState.chatRoom) {
        this.handleRoomOccupantUpdate(roomName, occupants)
      }
    })

    // console.log('RtcService - Room callbacks setup complete')
  }

  /**
   * Handles room occupant updates with debouncing
   *
   * Debounces room occupant updates to prevent excessive processing when multiple
   * updates arrive in quick succession. Calls processRoomOccupantUpdate after a
   * short delay.
   *
   * @param {string} roomName - Name of the room
   * @param {Object} occupants - Map of room occupants
   */
  handleRoomOccupantUpdate(roomName, occupants) {
    // Debounce room occupant updates to prevent rapid successive calls
    if (this.roomUpdateTimeout) {
      clearTimeout(this.roomUpdateTimeout)
    }

    this.roomUpdateTimeout = setTimeout(() => {
      this.processRoomOccupantUpdate(roomName, occupants)
    }, 100) // 100ms debounce
  }

  /**
   * Processes room occupant updates
   *
   * Updates the internal lists of coaches and candidates based on room occupants.
   * Filters out candidates with "Chat Ended" / away presence and notifies listeners.
   *
   * @param {string} roomName - Name of the room
   * @param {Object} occupants - Map of room occupants
   */
  processRoomOccupantUpdate(roomName, occupants) {
    this._lastOccupants = occupants

    // Process occupants and update coaches list
    const coaches = this.processOccupants(occupants)

    // Also process candidates for coach view
    const candidates = this.processCandidates(occupants)

    // Check if either coaches or candidates list has actually changed
    const currentCoaches = this.connectionState.coaches
    const currentCandidates = this.connectionState.candidates

    const coachesChanged =
      coaches.length !== currentCoaches.length ||
      coaches.some(
        (coach, index) =>
          !currentCoaches[index] ||
          currentCoaches[index].easyrtcid !== coach.easyrtcid ||
          currentCoaches[index].status !== coach.status ||
          currentCoaches[index].presence?.status !== coach.presence?.status ||
          currentCoaches[index].coachBackgroundMode !==
            coach.coachBackgroundMode,
      )

    const candidatesChanged =
      candidates.length !== currentCandidates.length ||
      candidates.some(
        (candidate, index) =>
          !currentCandidates[index] ||
          currentCandidates[index].easyrtcid !== candidate.easyrtcid ||
          currentCandidates[index].status !== candidate.status ||
          currentCandidates[index].presence?.status !==
            candidate.presence?.status,
      )

    if (!coachesChanged && !candidatesChanged) {
      return
    }

    // Always update coaches list - don't filter based on specific coach ID
    // The context needs to receive the coaches list to find the specified coach
    // Create new array references to trigger React re-renders
    this.connectionState.coaches = [...coaches]
    this.connectionState.candidates = [...candidates]

    // Notify listeners of the update
    this.notifyListeners('coachesUpdate', this.connectionState.coaches)
    this.notifyListeners('candidatesUpdate', this.connectionState.candidates)

    // Call the appropriate list functions based on user mode
    this.updateLists(roomName, occupants, coaches, candidates)
  }

  // Process candidates from room occupants
  /**
   * Processes candidate occupants from room data
   *
   * Filters and processes candidates from room occupants, excluding coaches.
   * Hides candidates who have left the waiting room (presence.show === 'away',
   * matching legacy chat.js) or finished a chat (status === 'Chat Ended').
   *
   * @param {Object} occupants - Map of room occupants
   * @returns {Array<Object>} Array of candidate objects
   */
  processCandidates(occupants) {
    if (!occupants) {
      return []
    }

    const candidates = []
    for (const easyrtcid in occupants) {
      const occupant = occupants[easyrtcid]
      const isCoach = occupant.apiField?.isCoach?.fieldValue

      // If not a coach, it's a candidate
      if (!isCoach) {
        // Match legacy coach candidate list: do not show away occupants.
        // Also hide "Chat Ended" (dnd) so finished chats do not linger in the list.
        if (
          occupant.presence?.show === 'away' ||
          occupant.presence?.status === 'Chat Ended'
        ) {
          continue
        }

        const startingChatAt = this._readOccupantNumericField(
          occupant,
          'startingChatAt',
        )
        const chatFrameJoined = Boolean(
          occupant.apiField?.chatFrameJoined?.fieldValue,
        )

        // Abandoned modal→frame handoff: frame never joined within TTL
        if (
          !chatFrameJoined &&
          startingChatAt &&
          Date.now() - startingChatAt > STARTING_CHAT_HANDOFF_TTL_MS
        ) {
          continue
        }

        const candidate = {
          easyrtcid,
          username: occupant.username,
          displayName: occupant.apiField?.displayName?.fieldValue
            ? decodeURIComponent(occupant.apiField.displayName.fieldValue)
            : occupant.username,
          firstName: occupant.apiField?.firstName?.fieldValue
            ? decodeURIComponent(occupant.apiField.firstName.fieldValue)
            : occupant.username,
          avatar: occupant.apiField?.avatar?.fieldValue
            ? decodeURIComponent(occupant.apiField.avatar.fieldValue)
            : 'https://www-yns-com.s3.amazonaws.com/images/icon_coach_generic.png',
          presence: occupant.presence,
          status: occupant.presence?.show || 'unknown',
          chatMode: occupant.apiField?.chatMode?.fieldValue,
          conversationId: occupant.apiField?.conversationId?.fieldValue,
          userLocale: occupant.apiField?.userLocale?.fieldValue,
          userLanguage: occupant.apiField?.userLanguage?.fieldValue,
          userCompany: occupant.apiField?.userCompany?.fieldValue,
          userCompanyId: occupant.apiField?.userCompanyId?.fieldValue,
          partnerCandidate: occupant.apiField?.partnerCandidate?.fieldValue,
          candidateId: occupant.apiField?.userId?.fieldValue || null,
          isMobile: occupant.apiField?.isMobile?.fieldValue,
          browser: occupant.apiField?.browser?.fieldValue,
          os: occupant.apiField?.os?.fieldValue,
          startingChatAt,
          chatFrameJoined,
        }

        candidates.push(candidate)
      }
    }

    this._scheduleHandoffTtlRefresh(occupants)

    return this._dedupeCandidatesByIdentity(candidates)
  }

  /**
   * @param {Object} occupant
   * @param {string} fieldName
   * @returns {number|null}
   * @private
   */
  _readOccupantNumericField(occupant, fieldName) {
    const raw = occupant.apiField?.[fieldName]?.fieldValue
    if (raw === null || raw === undefined || raw === '') {
      return null
    }
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  }

  /**
   * Presence precedence for collapsing duplicate connections of the same person.
   * Higher wins: frame-owned / in-call > Starting Chat > Coach List.
   *
   * @param {Object} candidate
   * @returns {number}
   * @private
   */
  _candidatePresenceScore(candidate) {
    let score = 0
    if (candidate.chatFrameJoined) {
      score += 100
    }
    if (candidate.presence?.show === 'dnd') {
      score += 50
    }
    const status = candidate.presence?.status || ''
    if (status === 'Starting Chat') {
      score += 30
    } else if (status === 'Coach List' || status === 'Chat List') {
      score += 10
    } else if (status) {
      // Conversation starter or in-chat status text
      score += 40
    }
    return score
  }

  /**
   * Keep one row per candidate identity (userId, else chat token username).
   *
   * @param {Array<Object>} candidates
   * @returns {Array<Object>}
   * @private
   */
  _dedupeCandidatesByIdentity(candidates) {
    const byKey = new Map()

    candidates.forEach((candidate) => {
      const key =
        candidate.candidateId || candidate.username || candidate.easyrtcid
      const existing = byKey.get(key)
      if (
        !existing ||
        this._candidatePresenceScore(candidate) >
          this._candidatePresenceScore(existing)
      ) {
        byKey.set(key, candidate)
      }
    })

    return Array.from(byKey.values())
  }

  /**
   * Reprocess occupants when an abandoned handoff TTL is about to expire,
   * so coaches do not wait for the next EasyRTC occupant event.
   *
   * @param {Object} occupants
   * @private
   */
  _scheduleHandoffTtlRefresh(occupants) {
    if (this._handoffTtlTimeout) {
      clearTimeout(this._handoffTtlTimeout)
      this._handoffTtlTimeout = null
    }

    const now = Date.now()
    let soonestExpiry = null

    Object.keys(occupants || {}).forEach((easyrtcid) => {
      const occupant = occupants[easyrtcid]
      if (occupant.apiField?.isCoach?.fieldValue) {
        return
      }
      if (occupant.apiField?.chatFrameJoined?.fieldValue) {
        return
      }
      const startingChatAt = this._readOccupantNumericField(
        occupant,
        'startingChatAt',
      )
      if (!startingChatAt) {
        return
      }
      const expiresAt = startingChatAt + STARTING_CHAT_HANDOFF_TTL_MS
      if (expiresAt <= now) {
        return
      }
      if (soonestExpiry === null || expiresAt < soonestExpiry) {
        soonestExpiry = expiresAt
      }
    })

    if (soonestExpiry === null) {
      return
    }

    this._handoffTtlTimeout = setTimeout(
      () => {
        this._handoffTtlTimeout = null
        if (this._lastOccupants && this.connectionState.chatRoom) {
          this.processRoomOccupantUpdate(
            this.connectionState.chatRoom,
            this._lastOccupants,
          )
        }
      },
      soonestExpiry - now + 50,
    )
  }

  /**
   * Sets room fields based on user mode
   *
   * Configures room fields (displayName, presence, etc.) based on the user's mode
   * (coach, candidate, or coach-list). Delegates to mode-specific methods.
   *
   * @param {Object} [options={}] - Options for setting room fields
   */
  setRoomFields(options) {
    // Set user mode based on options
    if (options.userMode) {
      this.connectionState.userMode = options.userMode
    }

    // Handle different user modes appropriately
    if (this.connectionState.userMode === 'coach') {
      this.setCoachRoomFields(options)
    } else if (this.connectionState.userMode === 'coach-list') {
      this.setCoachListRoomFields(options)
    } else {
      // All other user modes (candidate, chat-list-on-candidate, etc.) use candidate fields
      this.setCandidateRoomFields(options)
    }
  }

  /**
   * Sets room fields for coach mode
   *
   * Configures room fields specific to coaches, including display name, availability,
   * and presence status. Uses EasyRTC's setRoomField method to update room data.
   *
   * @param {Object} [options={}] - Options for coach room fields
   */
  setCoachRoomFields(options) {
    const roomName = this.connectionState.chatRoom

    // List identity (name/avatar) stays on room fields so the coach list paints
    // immediately. Heavy expand content (skills/accolades/bio/video) loads via
    // GET /coach-info using coachId.
    const fields = {
      coachId: options.userId || options.coachId || null,
      displayName: options.displayName || options.username,
      abbreviatedName:
        options.abbreviatedName || options.firstName || options.username,
      firstName: options.firstName || options.username,
      isCoach: true,
      isTechSupport: options.isTechSupport || false,
      videoEnabled: options.videoEnabled || false,
      avatar: options.avatar || DEFAULT_COACH_AVATAR,
      bigAvatar: options.bigAvatar || options.avatar || DEFAULT_COACH_AVATAR,
      coachLocales: options.coachLocales || ['en'],
      userCompanyId: options.userCompanyId,
      coachBackgroundMode: options.coachBackgroundMode || 'none', // 'none', 'blur', 'image'
      // CTS-9526: active text chat slots for candidate-side soft load balancing
      activeTextChatCount: options.activeTextChatCount ?? 0,
    }

    Object.entries(fields).forEach(([fieldName, fieldValue]) => {
      if (fieldValue !== null && fieldValue !== undefined) {
        this.easyrtc.setRoomApiField(roomName, fieldName, fieldValue)
      }
    })

    // Update presence based on availability setting - use correct presence states
    // Use the availability prop directly
    if (options.availability) {
      this.updateCoachPresence(
        options.availability,
        this.getAvailabilityPresenceMessage(options.availability),
      )
    } else {
      // Default to 'away' if no availability is specified
      this.updateCoachPresence('away', 'Unavailable')
    }
  }

  /**
   * Sets room fields for candidate mode
   *
   * Configures room fields specific to candidates, including display name and
   * presence status. Uses EasyRTC's setRoomField method to update room data.
   *
   * @param {Object} [options={}] - Options for candidate room fields
   */
  setCandidateRoomFields(options) {
    // console.log(
    //   'RtcService - setCandidateRoomFields called with options:',
    //   options,
    // )
    // Set all the room API fields for candidates (based on reference chat.js implementation)
    const fields = {
      videoEnabled: options.videoEnabled || false,
      chatMode: options.chatMode || 'text',
      displayName:
        options.userDisplayName || options.displayName || options.username,
      firstName: options.userFirstName || options.firstName || options.username,
      browser: options.browserName || options.browser || '',
      userLocale: options.userLocale || 'en',
      userLanguage: options.userLanguage || 'en',
      conversationId: options.conversationId || null,
      username: options.username,
      conversationStarter: options.conversationStarter || null,
      isMobile: options.isMobile || false,
      userCompany: options.userCompany || '',
      userCompanyId: options.userCompanyId || null,
      partnerCandidate: options.partnerCandidate || false,
      avatar:
        options.avatar ||
        'https://www-yns-com.s3.amazonaws.com/images/icon_coach_generic.png',
      os: options.os || '',
      deloitteUser: options.deloitteUser || false,
      userCareerDevelopment: options.userCareerDevelopment || false,
      outplacement: options.outplacement || false,
      opRedesign: options.opRedesign || false,
      userId: options.userId || null,
    }

    Object.entries(fields).forEach(([fieldName, fieldValue]) => {
      if (fieldValue !== null && fieldValue !== undefined) {
        this.easyrtc.setRoomApiField(
          this.connectionState.chatRoom,
          fieldName,
          fieldValue,
        )
      }
    })

    this.connectionState.candidateFieldOptions = options

    // Update connection state with user type information
    this.updateConnectionState({
      deloitteUser: options.deloitteUser || false,
      userCareerDevelopment: options.userCareerDevelopment || false,
      outplacement: options.outplacement || false,
      opRedesign: options.opRedesign || false,
    })

    // Update presence for candidates (based on chat.js)
    const presenceMessage = options.conversationStarter || 'Starting Chat'
    this.easyrtc.updatePresence('chat', presenceMessage, options.conversationId)
  }

  /**
   * Sets room fields for coach-list mode
   *
   * Configures room fields for the coach list view, including display name and
   * presence status. Uses EasyRTC's setRoomField method to update room data.
   *
   * @param {Object} [options={}] - Options for coach-list room fields
   */
  setCoachListRoomFields(options) {
    // Set specific fields for coach-list mode (candidate viewing coaches)
    // Based on legacy chat.js implementation for userMode === 'coach-list'
    const fields = {
      displayName:
        options.userDisplayName || options.displayName || options.username,
      userCompany: options.userCompany || '',
      userCompanyId: options.userCompanyId || null,
      partnerCandidate: options.partnerCandidate || false,
      userId: options.userId || null,
      browser:
        options.browserName ||
        options.browser ||
        (typeof navigator !== 'undefined' ? navigator.userAgent : '') ||
        '',
      isMobile: options.isMobile || false,
      os: options.os || '',
      // Do NOT set isCoach: true - this is a candidate!
    }

    // Set all room API fields
    Object.entries(fields).forEach(([fieldName, fieldValue]) => {
      if (fieldValue !== null && fieldValue !== undefined) {
        this.easyrtc.setRoomApiField(
          this.connectionState.chatRoom,
          fieldName,
          fieldValue,
        )
      }
    })

    // Set presence for coach-list mode (candidate viewing coaches)
    // Check if we're in chat_ended state, otherwise use "Coach List"
    const presenceMessage = options.presenceMessage || 'Coach List'
    if (presenceMessage === 'Chat Ended') {
      // Use 'dnd' state for chat ended to avoid filtering in legacy chat.js
      // Legacy chat.js filters out candidates with presence.show = "away"
      this.easyrtc.updatePresence('dnd', 'Chat Ended')
    } else {
      this.easyrtc.updatePresence('chat', 'Coach List')
    }
  }

  /**
   * Updates coach presence status in the room
   *
   * Updates the coach's presence status in the room to indicate their current
   * state (e.g., 'text_only', 'audio_video'). Uses EasyRTC's setRoomField method.
   *
   * @param {string} state - Presence state: 'text_only' or 'audio_video'
   * @param {string} statusText - Status text to display
   */
  updateCoachPresence(state, statusText) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    // Update presence in EasyRTC
    // For available coaches, set show to "chat" and status to the specific availability type
    if (state === 'text_only' || state === 'audio_video') {
      this.easyrtc.updatePresence('chat', state)
    } else if (state === 'dnd') {
      // Busy / at capacity — candidates filter busy on show === 'dnd'
      this.easyrtc.updatePresence('dnd', statusText || 'Busy')
    } else {
      // away / inactive
      this.easyrtc.updatePresence('away', statusText || 'Unavailable')
    }

    // Update local state
    this.connectionState.currentPresence = { state, statusText }

    // Notify listeners about presence change
    this.notifyListeners('presenceUpdated', { state, statusText })
  }

  /**
   * Publishes how many active text chats this coach currently has.
   * Used by candidates for CTS-9526 soft load balancing (does not change presence).
   *
   * @param {number} count - Number of active text chats (not pending, not A/V)
   */
  updateCoachActiveTextChatCount(count) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const normalizedCount = Number.isFinite(count) ? Math.max(0, count) : 0
    this.easyrtc.setRoomApiField(
      this.connectionState.chatRoom,
      'activeTextChatCount',
      normalizedCount,
    )
  }

  /**
   * Processes room occupants and extracts coach information
   *
   * Filters room occupants to identify coaches and extracts their information
   * including display names, availability, and other metadata. Sorts coaches
   * alphabetically by display name.
   *
   * @param {Object} occupants - Map of room occupants
   * @returns {Array<Object>} Array of coach objects with extracted information
   */
  processOccupants(occupants) {
    if (!occupants) {
      return []
    }

    const coaches = []
    for (const easyrtcid in occupants) {
      const occupant = occupants[easyrtcid]

      const isCoach = occupant.apiField?.isCoach?.fieldValue

      if (isCoach) {
        const coachId = occupant.apiField?.coachId?.fieldValue || null
        const displayName = occupant.apiField?.displayName?.fieldValue
          ? decodeURIComponent(occupant.apiField.displayName.fieldValue)
          : null
        const avatar = occupant.apiField?.avatar?.fieldValue
          ? decodeURIComponent(occupant.apiField.avatar.fieldValue)
          : null
        const bigAvatar = occupant.apiField?.bigAvatar?.fieldValue
          ? decodeURIComponent(occupant.apiField.bigAvatar.fieldValue)
          : null

        const coach = {
          easyrtcid,
          username: occupant.username,
          coachId,
          displayName: displayName || occupant.username,
          firstName: occupant.apiField?.firstName?.fieldValue
            ? decodeURIComponent(occupant.apiField.firstName.fieldValue)
            : null,
          abbreviatedName: occupant.apiField?.abbreviatedName?.fieldValue
            ? decodeURIComponent(occupant.apiField.abbreviatedName.fieldValue)
            : null,
          avatar: avatar || DEFAULT_COACH_AVATAR,
          bigAvatar: bigAvatar || avatar || DEFAULT_COACH_AVATAR,
          isTechSupport: occupant.apiField?.isTechSupport?.fieldValue,
          coachLocales: occupant.apiField?.coachLocales?.fieldValue,
          // Skills/accolades/bio come from coach-info API (legacy room fallback below)
          coachSkills: occupant.apiField?.coachSkills?.fieldValue || [],
          coachAccolades: occupant.apiField?.coachAccolades?.fieldValue,
          coachBio: occupant.apiField?.coachBio?.fieldValue,
          companyId: occupant.apiField?.userCompanyId?.fieldValue,
          presence: occupant.presence,
          status: occupant.presence?.show || 'unknown',
          // CTS-9526: default 0 when absent (older coach clients / rollouts)
          activeTextChatCount:
            occupant.apiField?.activeTextChatCount?.fieldValue ?? 0,
          coachBackgroundMode:
            occupant.apiField?.coachBackgroundMode?.fieldValue || 'none',
        }

        // Debug logging for coach background mode
        /*
        console.log('RtcService - Processing coach with background mode:', {
          easyrtcid,
          displayName: coach.displayName,
          coachBackgroundMode: coach.coachBackgroundMode,
          apiField: occupant.apiField?.coachBackgroundMode,
        })
        */

        coaches.push(coach)
      } else {
        // Not a coach, skip
      }
    }

    // Sort coaches by display name (legacy chat doesn't show current user)
    coaches.sort((a, b) => {
      return (a.displayName || a.username).localeCompare(
        b.displayName || b.username,
      )
    })

    return coaches
  }

  // Get initial coaches list when joining room
  /**
   * Updates the coaches list from room occupants
   *
   * Retrieves current room occupants and updates the internal coaches list.
   * Notifies listeners of any changes to the coaches list.
   */
  updateCoachesFromRoom() {
    if (this.easyrtc && this.connectionState.chatRoom) {
      const roomOccupants = this.easyrtc.getRoomOccupantsAsMap(
        this.connectionState.chatRoom,
      )

      if (!roomOccupants) {
        return
      }

      const coaches = this.processOccupants(roomOccupants)
      // Create new array reference to trigger React re-renders
      this.connectionState.coaches = [...coaches]
      this.notifyListeners('coachesUpdate', this.connectionState.coaches)
    }
  }

  // Utility methods
  /**
   * Generates a unique conversation ID
   *
   * Creates a unique identifier for a conversation using timestamp and random string.
   * Format: "conv_{timestamp}_{randomString}"
   *
   * @returns {string} A unique conversation ID
   */
  generateConversationId() {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // Get coach name from room occupants
  /**
   * Gets the display name of a coach by EasyRTC ID
   *
   * Retrieves the coach's display name from room occupant data. Falls back to
   * first name or username if display name is not available.
   *
   * @param {string} easyrtcid - EasyRTC ID of the coach
   * @returns {string|null} The coach's display name, or null if not found
   */
  getCoachName(easyrtcid) {
    if (this.connectionState.chatRoom) {
      const occupants = this.easyrtc.getRoomOccupantsAsMap(
        this.connectionState.chatRoom,
      )
      const occupant = occupants[easyrtcid]
      if (occupant && occupant.apiField && occupant.apiField.displayName) {
        return decodeURIComponent(occupant.apiField.displayName.fieldValue)
      }
      return occupant ? occupant.username : 'Coach'
    }
    return 'Coach'
  }

  // Get appropriate presence message based on availability state
  /**
   * Gets the appropriate presence message based on availability state
   *
   * Converts an availability state (e.g., 'available', 'busy') into a human-readable
   * presence message for display in the UI.
   *
   * @param {string} availability - Availability state
   * @returns {string} Presence message text
   */
  getAvailabilityPresenceMessage(availability) {
    switch (availability) {
      case 'text_only':
        return 'Available for text chat'
      case 'audio_video':
        return 'Available for audio, video, & text chat'
      case 'away':
      default:
        return 'Unavailable'
    }
  }

  // Update current user's status in the coaches list
  /**
   * Updates the current user's status in the coaches list
   *
   * Updates the availability status of the current user in the internal coaches list.
   * Used to reflect status changes in the UI.
   *
   * @param {string} availability - New availability status
   */
  updateCurrentUserStatus(availability) {
    if (this.connectionState.coaches) {
      const currentUserIndex = this.connectionState.coaches.findIndex(
        (coach) => coach.isCurrentUser,
      )

      if (currentUserIndex !== -1) {
        // Mirror EasyRTC presence.show semantics used by candidate filters
        let show = 'away'
        let status = availability
        if (availability === 'text_only' || availability === 'audio_video') {
          show = 'chat'
          status = availability
        } else if (availability === 'dnd') {
          show = 'dnd'
          status = 'Busy'
        } else {
          show = 'away'
          status = 'Unavailable'
        }

        this.connectionState.coaches[currentUserIndex].status = show
        this.connectionState.coaches[currentUserIndex].presence = {
          show,
          status,
        }

        // Notify listeners about the update
        this.notifyListeners('coachesUpdate', this.connectionState.coaches)
      }
    }
  }

  // Get appropriate presence message based on call type
  /**
   * Gets the presence message for a call type
   *
   * Generates an appropriate presence message based on the call type (text, audio, video)
   * and target participant. Used for updating presence status in the room.
   *
   * @param {string} callType - Type of call: 'text', 'audio', or 'video'
   * @param {string} targetEasyrtcid - EasyRTC ID of the target participant
   * @returns {string} Presence message text
   */
  getPresenceMessage(callType, targetEasyrtcid) {
    const coachName = this.getCoachName(targetEasyrtcid)

    switch (callType) {
      case 'video':
        return `In video chat with ${coachName}`
      case 'audio':
        return `In audio chat with ${coachName}`
      case 'text':
      default:
        return `In text chat with ${coachName}`
    }
  }

  // Event listener management
  /**
   * Adds an event listener for RTC-related events
   *
   * Registers a callback function to be called when a specific event occurs.
   * Supported events include: 'connected', 'disconnected', 'remoteStreamReceived',
   * 'streamClosed', 'callAccepted', 'callRejected', 'callMissed', 'callCancelled',
   * 'chatEndedByCoach', 'coachTyping', 'candidateTyping', 'peerMessage', 'roomUpdate',
   * 'coachesUpdated', 'candidatesUpdated', 'autoplayBlocked', and more.
   *
   * @param {string} event - The event name to listen for
   * @param {Function} callback - The callback function to invoke when the event occurs
   */
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  /**
   * Removes an event listener
   *
   * Unregisters a previously registered callback function for a specific event.
   *
   * @param {string} event - The event name
   * @param {Function} callback - The callback function to remove
   */
  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }

  /**
   * Notifies all registered listeners of an event
   *
   * Invokes all callback functions registered for the given event with the provided data.
   * Errors in individual callbacks are caught and logged without affecting other listeners.
   *
   * @param {string} event - The event name
   * @param {*} data - Data to pass to the event listeners
   */
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      /*
      console.log(
        `RtcService - Notifying ${this.listeners.get(event).size} listeners for event: ${event}`,
        data,
      )
      */
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${event} listener:`, error)
        }
      })
    } else {
      // console.log(`RtcService - No listeners found for event: ${event}`)
    }
  }

  // Heartbeat monitoring methods
  /**
   * Starts heartbeat monitoring for coach presence
   *
   * Begins sending periodic heartbeat messages and checking for coach heartbeat
   * to detect disconnections. Used to monitor coach availability in candidate mode.
   * Enhanced for mobile with more frequent checks and connection verification.
   */
  startHeartbeatMonitoring() {
    // Start checking coach heartbeat every 30 seconds (reduced from default for mobile)
    this.heartbeatCheckInterval = setInterval(() => {
      this.checkCoachHeartbeat()
    }, 30000) // Check every 30 seconds

    // Set up peer disconnection monitoring using correct EasyRTC API
    this.setPeerClosedListener()

    // Enhanced heartbeat: send periodic pings to detect WebSocket disconnections
    // This is especially important on mobile where WebSocket may disconnect silently
    this.heartbeatInterval = setInterval(() => {
      this._sendHeartbeatPing()
    }, 15000) // Send heartbeat every 15 seconds
  }

  /**
   * Sends a heartbeat ping to verify connection is alive
   *
   * Sends a lightweight message over the data channel or WebSocket to verify
   * the connection is still active. This helps detect silent disconnections
   * on mobile devices.
   *
   * @private
   */
  _sendHeartbeatPing() {
    if (!this.easyrtc || !this.connectionState.isConnected) {
      return
    }

    // Check if WebSocket is still connected
    const isWebSocketConnected = this.easyrtc.webSocketConnected || false

    if (!isWebSocketConnected) {
      // WebSocket disconnected, notify listeners
      this._connectionLostDueToVisibility = true
      this.connectionState.isConnected = false
      this.notifyListeners('connectionLost', {
        reason: 'websocket_disconnected',
        timestamp: new Date().toISOString(),
      })

      // If page is visible, attempt reconnection
      if (document.visibilityState === 'visible') {
        this._handleVisibilityReconnection()
      }
    }
  }

  /**
   * Stops heartbeat monitoring
   *
   * Clears all heartbeat intervals and stops monitoring coach presence.
   */
  stopHeartbeatMonitoring() {
    if (this.heartbeatCheckInterval) {
      clearInterval(this.heartbeatCheckInterval)
      this.heartbeatCheckInterval = null
    }

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  /**
   * Checks if coach heartbeat is still active
   *
   * Verifies that the coach is still sending heartbeat messages. If no heartbeat
   * is detected, triggers coach disconnection handling.
   *
   * @private
   */
  checkCoachHeartbeat() {
    this._checkCoachConnection()
  }

  /**
   * One-off check that coach is still in room / peer connected. Call when candidate
   * app resumes (e.g. visibilitychange to visible) to detect disconnect during background.
   */
  checkCoachConnectionOnResume() {
    this._checkCoachConnection()
  }

  _checkCoachConnection() {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const room = this.easyrtc.getRoomOccupantsAsMap(
      this.connectionState.chatRoom,
    )

    if (!this.connectionState.coachEasyRtcId) return

    // Room null/undefined likely means we lost connection to the server (e.g. after app was backgrounded)
    if (!room) {
      this.handleCoachPeerDisconnection(this.connectionState.coachEasyRtcId)
      return
    }

    const coachStillPresent = room[this.connectionState.coachEasyRtcId]
    const peerConnected = this.easyrtc.isPeerInAnyRoom(
      this.connectionState.coachEasyRtcId,
    )

    if (!coachStillPresent) {
      this.handleCoachDisconnection('coach_left_room')
    } else if (!peerConnected) {
      this.handleCoachPeerDisconnection(this.connectionState.coachEasyRtcId)
    }
  }

  /**
   * Sets up peer closed listener
   *
   * Configures a listener for when a peer connection is closed. Used to detect
   * when the coach disconnects in candidate mode.
   *
   * @private
   */
  setPeerClosedListener() {
    if (!this.easyrtc) return

    // Set up peer disconnection handler using the correct EasyRTC API
    this.easyrtc.setPeerClosedListener((easyrtcid) => {
      // If this is our coach disconnecting
      if (easyrtcid === this.connectionState.coachEasyRtcId) {
        this.handleCoachPeerDisconnection(easyrtcid)
      }
    })
  }

  /**
   * Handles coach disconnection
   *
   * Called when the coach disconnects. Stops heartbeat monitoring and notifies
   * listeners of the disconnection.
   *
   * @param {string} [reason='unknown'] - Reason for disconnection
   */
  handleCoachDisconnection(reason = 'unknown') {
    // Don't log event here - let CandidateChatManager handle logging with sessionStorage guards
    // to prevent duplicate events

    // Notify listeners (CandidateChatManager will handle the logging)
    this.notifyListeners('coachDisconnected', {
      reason,
      coachEasyrtcid: this.connectionState.coachEasyRtcId,
      timestamp: new Date().toISOString(),
    })
  }

  /**
   * Handles coach peer disconnection event
   *
   * Called by EasyRTC when the peer connection to the coach is closed. Verifies
   * it's the expected coach and triggers disconnection handling.
   *
   * @param {string} easyrtcid - EasyRTC ID of the disconnected peer
   */
  handleCoachPeerDisconnection(easyrtcid) {
    // Don't log event here - let CandidateChatManager handle logging with sessionStorage guards
    // to prevent duplicate events

    // Notify listeners (CandidateChatManager will handle the logging)
    this.notifyListeners('coachPeerDisconnected', {
      coachEasyrtcid: easyrtcid,
      reason: 'peer_connection_lost',
      timestamp: new Date().toISOString(),
    })
  }

  // Event logging function (similar to old chat.js pattern)
  /**
   * Logs a chat event to the server
   *
   * Sends a chat event (e.g., message sent, call started) to the server for logging
   * and analytics. Requires logChatEventPath to be configured via setLoggingConfig().
   *
   * @param {string} eventType - Type of event to log
   * @param {Object} info - Additional event information
   */
  logEvent(eventType, info) {
    if (!this.logChatEventPath) {
      return
    }

    const ts = new Date().getTime()
    const nts = ts + this.serverTimeOffset
    const params = {
      event_type: eventType,
      occurred_at: ts,
      event_info: info,
      chat_token: this.connectionState.myEasyrtcid || 'unknown',
      normalized_occurred_at: nts,
    }

    // Use fetch instead of jQuery for modern approach
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, 5000) // 5 second timeout

    fetch(this.logChatEventPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '',
      },
      body: JSON.stringify({
        chat_event: params,
      }),
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((data) => {
        return data
      })
      .catch((_error) => {
        clearTimeout(timeoutId)
      })
  }

  // Set logging configuration
  /**
   * Sets logging configuration
   *
   * Configures the API path for logging chat events and the server time offset
   * for synchronizing timestamps.
   *
   * @param {string} logChatEventPath - API path for logging chat events
   * @param {number} [serverTimeOffset=0] - Server time offset in milliseconds
   */
  setLoggingConfig(logChatEventPath, serverTimeOffset = 0) {
    this.logChatEventPath = logChatEventPath
    this.serverTimeOffset = serverTimeOffset
  }

  /**
   * Sets up visibility change handlers for mobile reconnection
   *
   * Listens for page visibility changes (tab backgrounding/foregrounding) and
   * handles reconnection when the page becomes visible again. This is critical
   * for mobile devices where the browser may throttle WebRTC when backgrounded.
   *
   * @private
   */
  _setupVisibilityHandlers() {
    if (typeof document === 'undefined') return

    this._visibilityChangeHandler = () => {
      this._lastVisibilityChange = new Date()

      if (document.visibilityState === 'visible') {
        // Page became visible - check if we need to reconnect
        if (this.connectionState.isConnected && this.easyrtc) {
          // Check if WebSocket is still connected
          const isWebSocketConnected = this.easyrtc.webSocketConnected || false

          if (!isWebSocketConnected || this._connectionLostDueToVisibility) {
            // Connection was lost while backgrounded, attempt to reconnect
            this._handleVisibilityReconnection()
          } else {
            // Connection appears healthy, but verify with a heartbeat check
            this._verifyConnectionAfterVisibility()
          }
        }
      } else if (document.visibilityState === 'hidden') {
        // Page became hidden - mark that we might lose connection
        this._connectionLostDueToVisibility = false
      }
    }

    document.addEventListener('visibilitychange', this._visibilityChangeHandler)
  }

  /**
   * Handles reconnection after page becomes visible
   *
   * Attempts to reconnect to the EasyRTC server if the connection was lost
   * while the page was backgrounded. Bounded by _maxReconnectAttempts with
   * exponential backoff so a dead signaling server doesn't get hammered by
   * every open tab - EasyRTC's vendored client has no .reconnect() method, so
   * this rebuilds the connection from scratch via _performConnection using
   * the params stored from the last successful connect().
   *
   * @private
   */
  _handleVisibilityReconnection() {
    if (this._isReconnecting) return

    if (!this._connectionParams) {
      // Never connected yet - nothing to rebuild
      return
    }

    if (this._reconnectAttempts >= this._maxReconnectAttempts) {
      this.notifyListeners('reconnectionFailed', {
        error: new Error('Max reconnection attempts reached'),
      })
      return
    }

    this._isReconnecting = true
    this._connectionLostDueToVisibility = false

    // Notify listeners that we're attempting to reconnect
    this.notifyListeners('reconnecting', {
      reason: 'visibility_change',
      attempt: this._reconnectAttempts + 1,
      timestamp: new Date().toISOString(),
    })

    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout)
    }

    // Exponential backoff (500ms, 1s, 2s, 4s, 8s) so retries space out instead
    // of hammering the signaling/TURN server on every heartbeat tick
    const delay = 500 * 2 ** this._reconnectAttempts

    this._reconnectTimeout = setTimeout(() => {
      this._reconnectAttempts += 1

      if (this.easyrtc) {
        try {
          this.easyrtc.disconnect()
        } catch (_error) {
          // Socket may already be closed - safe to ignore
        }
      }

      const { socketUrl, username, chatRoom, options } = this._connectionParams

      this._performConnection(socketUrl, username, chatRoom, options)
        .then((state) => {
          this._isReconnecting = false
          this.notifyListeners('reconnected', state)
          return state
        })
        .catch((error) => {
          console.warn('RtcService - Reconnection attempt failed:', error)
          this._isReconnecting = false
          this.notifyListeners('reconnectionFailed', {
            error,
            attempt: this._reconnectAttempts,
          })
        })
    }, delay)
  }

  /**
   * Verifies connection health after page becomes visible
   *
   * Sends a heartbeat or checks connection status to ensure the WebRTC
   * connection is still active after the page becomes visible.
   *
   * @private
   */
  _verifyConnectionAfterVisibility() {
    if (!this.easyrtc || !this.connectionState.chatRoom) return

    // Check if we can still access room occupants (indicates connection is alive)
    try {
      const occupants = this.easyrtc.getRoomOccupantsAsMap(
        this.connectionState.chatRoom,
      )
      if (!occupants) {
        // Connection appears dead, trigger reconnection
        this._handleVisibilityReconnection()
      }
    } catch (error) {
      console.warn('RtcService - Connection verification failed:', error)
      this._handleVisibilityReconnection()
    }
  }

  /**
   * True when the active element is a mailto:/tel: link.
   * Chromium fires beforeunload/pagehide for those protocol navigations even
   * when the document stays open; disconnecting would drop the chat.
   *
   * @returns {boolean}
   * @private
   */
  _isProtocolHandlerNavigation() {
    if (typeof document === 'undefined') {
      return false
    }

    const href = document.activeElement?.getAttribute?.('href') || ''
    return href.startsWith('mailto:') || href.startsWith('tel:')
  }

  /**
   * True during modal/selection → video-chat-frame handoff.
   * Skip unload disconnect so coaches keep seeing Starting Chat across navigation.
   *
   * @returns {boolean}
   * @private
   */
  _isChatHandoffNavigation() {
    return typeof window !== 'undefined' && window.leavingRoom === true
  }

  /**
   * Sets up page unload handlers for cleanup
   *
   * Ensures WebRTC connections are properly closed when the page is unloaded
   * or navigated away from. This prevents duplicate connections on refresh.
   *
   * @private
   */
  _setupUnloadHandlers() {
    if (typeof window === 'undefined') return

    this._beforeUnloadHandler = () => {
      if (
        this._isProtocolHandlerNavigation() ||
        this._isChatHandoffNavigation()
      ) {
        return
      }

      // Notify listeners about beforeunload so CandidateChatManager can send left_chat action
      this.notifyListeners('beforeUnload', {
        myEasyrtcid: this.connectionState.myEasyrtcid,
        userMode: this.connectionState.userMode,
      })

      // Clean disconnect before page unloads
      if (this.easyrtc && this.connectionState.isConnected) {
        try {
          // Use a synchronous approach for beforeunload
          this.easyrtc.disconnect()
        } catch (error) {
          console.warn(
            'RtcService - Error during beforeunload disconnect:',
            error,
          )
        }
      }
    }

    this._pageHideHandler = () => {
      if (
        this._isProtocolHandlerNavigation() ||
        this._isChatHandoffNavigation()
      ) {
        return
      }

      // Notify listeners about pagehide so CandidateChatManager can send left_chat action
      this.notifyListeners('pageHide', {
        myEasyrtcid: this.connectionState.myEasyrtcid,
        userMode: this.connectionState.userMode,
      })

      // Also handle pagehide event (more reliable on mobile)
      if (this.easyrtc && this.connectionState.isConnected) {
        try {
          this.easyrtc.disconnect()
        } catch (error) {
          console.warn('RtcService - Error during pagehide disconnect:', error)
        }
      }
    }

    window.addEventListener('beforeunload', this._beforeUnloadHandler)
    window.addEventListener('pagehide', this._pageHideHandler)
  }

  /**
   * Cleans up visibility and unload handlers
   *
   * Removes event listeners to prevent memory leaks.
   *
   * @private
   */
  _cleanupHandlers() {
    if (this._visibilityChangeHandler && typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this._visibilityChangeHandler,
      )
      this._visibilityChangeHandler = null
    }

    if (this._beforeUnloadHandler && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler)
      this._beforeUnloadHandler = null
    }

    if (this._pageHideHandler && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this._pageHideHandler)
      this._pageHideHandler = null
    }

    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout)
      this._reconnectTimeout = null
    }
  }

  // Cleanup
  /**
   * Disconnects from the EasyRTC server
   *
   * Closes the connection, stops heartbeat monitoring, clears timeouts, and resets
   * connection state. Should be called when the service is no longer needed.
   */
  disconnect() {
    // Stop heartbeat monitoring
    this.stopHeartbeatMonitoring()

    // Clean up handlers
    this._cleanupHandlers()

    if (this.easyrtc) {
      this.easyrtc.disconnect()
    }

    // Clear any pending room updates
    if (this.roomUpdateTimeout) {
      clearTimeout(this.roomUpdateTimeout)
      this.roomUpdateTimeout = null
    }

    this.connectionState.isConnected = false
    this._isReconnecting = false
    this._connectionLostDueToVisibility = false
    this._connectionParams = null
    this._reconnectAttempts = 0
    this.notifyListeners('disconnected', this.connectionState)
  }

  // Getters for other managers
  /**
   * Gets the EasyRTC client instance
   *
   * Returns the underlying EasyRTC client instance for direct API access when needed.
   * Use with caution - prefer using RtcService methods when possible.
   *
   * @returns {Object|null} The EasyRTC client instance, or null if not connected
   */
  getEasyRTC() {
    return this.easyrtc
  }

  // Wait for Open-EasyRTC to be ready
  /**
   * Waits for Open-EasyRTC to be ready
   *
   * Polls for the Open-EasyRTC library to be loaded and ready before attempting
   * to use it. This is necessary because the library may load asynchronously.
   *
   * @returns {Promise<void>} Promise that resolves when Open-EasyRTC is ready
   * @private
   */
  async _waitForOpenEasyRTC() {
    return new Promise((resolve) => {
      const checkReady = () => {
        if (window.openEasyRtc && window.openEasyRtcReady) {
          resolve()
        } else {
          setTimeout(checkReady, 50)
        }
      }
      checkReady()
    })
  }

  /**
   * Gets the remote media stream
   *
   * Returns the currently active remote media stream (the other participant's stream).
   *
   * @returns {MediaStream|null} The remote media stream, or null if not available
   */
  getRemoteStream() {
    return this.remoteStream
  }

  // Get local stream for caller video preview
  /**
   * Gets the local media stream
   *
   * Retrieves the local media stream from EasyRTC if available.
   *
   * @returns {MediaStream|null} The local media stream, or null if not available
   */
  getLocalStream() {
    if (this.easyrtc) {
      return this.easyrtc.getLocalStream()
    }
    return null
  }

  // Initiate a call to a specific coach
  /**
   * Initiates a call to a coach
   *
   * Initiates a WebRTC call to the specified coach using EasyRTC's call method.
   * Supports video and audio call types. This is a low-level method - prefer using
   * CallService.initiateCall() for higher-level call management.
   *
   * See EasyRTC call documentation:
   * https://open-easyrtc.github.io/open-easyrtc/client/Easyrtc.html#call
   *
   * @param {string} targetEasyrtcid - EasyRTC ID of the coach to call
   * @param {string} [callType='video'] - Type of call: 'video' or 'audio'
   * @returns {Promise<Object>} Promise that resolves when call is initiated
   */
  async callCoach(targetEasyrtcid, callType = 'video') {
    if (!this.easyrtc || !this.connectionState.isConnected) {
      throw new Error('Not connected to EasyRTC')
    }

    return new Promise((resolve, reject) => {
      const successCB = () => {
        this.notifyListeners('callInitiated', { targetEasyrtcid, callType })
        resolve({ targetEasyrtcid, callType })
      }

      const failureCB = (_errorCode, errorText) => {
        reject(new Error(`Call failed: ${errorText}`))
      }

      const acceptedCB = (accepted, easyrtcid) => {
        if (accepted) {
          this.notifyListeners('callAccepted', { fromEasyrtcid: easyrtcid })
        } else {
          this.notifyListeners('callRejected', { fromEasyrtcid: easyrtcid })
        }
      }

      // Initiate the call
      this.easyrtc.call(targetEasyrtcid, successCB, failureCB, acceptedCB)
    })
  }

  // Hang up all active calls
  /**
   * Hangs up all active calls
   *
   * Terminates all active WebRTC calls using EasyRTC's hangupAll method.
   *
   * See EasyRTC hangup documentation:
   * https://open-easyrtc.github.io/open-easyrtc/client/Easyrtc.html#hangupAll
   */
  hangupAll() {
    if (this.easyrtc) {
      this.easyrtc.hangupAll()
      this.notifyListeners('callEnded', { reason: 'userHangup' })
    }
  }

  /**
   * Releases local camera/microphone and clears video elements.
   *
   * Mirrors legacy chat.js cleanup via easyrtc.closeLocalMediaStream.
   *
   * @param {Object} [options]
   * @param {string} [options.conversationId] - Conversation/stream name for EasyRTC
   * @param {HTMLVideoElement} [options.selfVideo] - Local preview element
   * @param {HTMLVideoElement} [options.callerVideo] - Remote preview element
   */
  releaseLocalMedia({
    conversationId = null,
    selfVideo = null,
    callerVideo = null,
  } = {}) {
    if (!this.easyrtc) return

    const clearVideoElement = (element) => {
      if (!element) return

      try {
        if (typeof this.easyrtc.clearMediaStream === 'function') {
          this.easyrtc.clearMediaStream(element)
        }
      } catch (error) {
        console.warn('RtcService - Error clearing media stream element:', error)
      }

      element.srcObject = null

      try {
        if (typeof this.easyrtc.setVideoObjectSrc === 'function') {
          this.easyrtc.setVideoObjectSrc(element, '', conversationId)
        }
      } catch (error) {
        console.warn('RtcService - Error clearing video object source:', error)
      }
    }

    try {
      if (typeof this.easyrtc.closeLocalMediaStream === 'function') {
        if (conversationId) {
          this.easyrtc.closeLocalMediaStream(conversationId)
        }
        this.easyrtc.closeLocalMediaStream('default')
      }
    } catch (error) {
      console.warn('RtcService - Error closing local media stream:', error)
    }

    clearVideoElement(selfVideo || document.getElementById('selfVideo'))
    clearVideoElement(callerVideo || document.getElementById('callerVideo'))

    this.remoteStream = null
  }

  // Set up call acceptance checking for incoming calls
  /**
   * Sets up call acceptance handlers
   *
   * Configures EasyRTC call acceptance callbacks to handle incoming calls.
   * Determines whether to accept or reject calls based on current state.
   *
   * @private
   */
  setupCallAcceptance() {
    if (!this.easyrtc) return

    this.easyrtc.setAcceptChecker((easyrtcid, acceptor) => {
      /*
      console.log(
        'RtcService - setAcceptChecker called for EasyRTC call from:',
        easyrtcid,
      )
      */

      // This is an actual EasyRTC peer connection call, not a call_start message
      // Send a different event type so CoachChatManager can handle it appropriately
      this.notifyListeners('easyrtcCall', {
        fromEasyrtcid: easyrtcid,
        acceptCallback: acceptor,
        rejectCallback: () => acceptor(false),
      })
    })
  }

  // Update connection state and notify listeners
  /**
   * Updates the connection state
   *
   * Merges the provided updates into the current connection state and notifies
   * listeners of the state change.
   *
   * @param {Object} updates - Object with state properties to update
   */
  updateConnectionState(updates) {
    this.connectionState = { ...this.connectionState, ...updates }
    this.notifyListeners('stateChange', this.connectionState)
  }

  /**
   * Gets a copy of the current connection state
   *
   * Returns a shallow copy of the connection state object. Modifications to the
   * returned object will not affect the internal state.
   *
   * @returns {Object} A copy of the connection state
   */
  getConnectionState() {
    return { ...this.connectionState }
  }

  // Update the appropriate lists based on user mode and context
  /**
   * Updates coaches and candidates lists
   *
   * Updates the internal lists of coaches and candidates based on the provided data.
   * Delegates to mode-specific list update methods (listCoaches, listChatUsers).
   *
   * @param {string} _roomName - Room name (currently unused)
   * @param {Object} _occupants - Room occupants (currently unused)
   * @param {Array<Object>} coaches - Array of coach objects
   * @param {Array<Object>} candidates - Array of candidate objects
   */
  updateLists(_roomName, _occupants, coaches, candidates) {
    // Get the current user mode from connection state or options
    const userMode = this.connectionState.userMode || 'coach'

    if (userMode === 'coach-list') {
      this.listCoaches(coaches)
    } else if (
      userMode === 'chat-list' ||
      userMode === 'chat-list-on-candidate'
    ) {
      this.listChatUsers(coaches, candidates)
    } else if (userMode === 'coach') {
      // For coaches, we need to populate candidate info for incoming calls
      this.populateCandidateInfo(candidates)
      // Also list coaches so the coach can see themselves and other coaches
      this.listCoaches(coaches)
    }
  }

  // List coaches (similar to legacy listCoaches function)
  /**
   * Updates the coaches list and notifies listeners
   *
   * Updates the internal coaches list and notifies listeners of the change.
   * Used by the coach-list mode to update the available coaches.
   *
   * @param {Array<Object>} coaches - Array of coach objects
   */
  listCoaches(coaches) {
    // Notify listeners about coach list update
    this.notifyListeners('coachListUpdated', {
      coaches,
      totalCount: coaches.length,
      availableCount: coaches.filter((c) => c.status === 'chat').length,
      busyCount: coaches.filter((c) => c.status === 'dnd').length,
    })
  }

  // List chat users (similar to legacy listChatUsers function)
  /**
   * Updates both coaches and candidates lists
   *
   * Updates the internal lists of coaches and candidates and notifies listeners.
   * Used when both lists need to be updated simultaneously.
   *
   * @param {Array<Object>} coaches - Array of coach objects
   * @param {Array<Object>} candidates - Array of candidate objects
   */
  listChatUsers(coaches, candidates) {
    // Notify listeners about chat users update
    this.notifyListeners('chatUsersUpdated', {
      coaches,
      candidates,
      totalCount: coaches.length,
      totalCandidates: candidates.length,
    })
  }

  // Populate candidate info for coaches (similar to legacy occupantListener logic)
  /**
   * Populates candidate information cache
   *
   * Extracts and stores candidate information from the candidates array for
   * quick lookup. This is used by other parts of the system that need candidate data.
   *
   * @param {Array<Object>} candidates - Array of candidate objects
   */
  populateCandidateInfo(candidates) {
    // Notify listeners about candidate info update
    this.notifyListeners('candidateInfoUpdated', {
      candidates,
      totalCount: candidates.length,
    })
  }

  // Force refresh room occupants (can be called from ChatManager)
  /**
   * Forces a refresh of room occupants
   *
   * Manually triggers a refresh of room occupant data from the EasyRTC server.
   * This can be useful when occupant data may be stale or needs to be synchronized.
   */
  forceRefreshRoomOccupants() {
    if (this.easyrtc && this.connectionState.chatRoom) {
      const roomOccupants = this.easyrtc.getRoomOccupantsAsMap(
        this.connectionState.chatRoom,
      )
      this._lastOccupants = roomOccupants

      // Process and update both coaches and candidates
      const coaches = this.processOccupants(roomOccupants)
      const candidates = this.processCandidates(roomOccupants)

      // Update connection state
      this.connectionState.coaches = coaches
      this.connectionState.candidates = candidates

      // Notify listeners
      this.notifyListeners('coachesUpdate', coaches)
      this.notifyListeners('candidatesUpdate', candidates)
    }
  }

  // Update candidate presence for different scenarios to match legacy chat.js behavior

  /**
   * Updates candidate presence when viewing coach list
   *
   * Sets the candidate's presence to indicate they are viewing the coach list.
   * Uses EasyRTC's updatePresence method.
   *
   * See EasyRTC presence documentation:
   * https://open-easyrtc.github.io/open-easyrtc/client/Easyrtc.html#updatePresence
   */
  updateCandidatePresenceCoachList() {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }
    this.easyrtc.updatePresence('chat', 'Coach List')

    this.connectionState.currentPresence = {
      state: 'chat',
      statusText: 'Coach List',
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Re-syncs candidate room API fields immediately before initiating a call.
   * Ensures browser/chatMode/conversationId are visible to coaches when call_start arrives.
   *
   * @param {Object} options
   * @param {string} options.chatMode - Call type: text, audio, or video
   * @param {string|null} [options.conversationId] - Conversation ID for this call
   */
  syncCandidateCallRoomFields({ chatMode, conversationId = null }) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const storedOptions = this.connectionState.candidateFieldOptions || {}
    const browser =
      storedOptions.browserName ||
      storedOptions.browser ||
      (typeof navigator !== 'undefined' ? navigator.userAgent : '') ||
      ''

    const fields = {
      videoEnabled: storedOptions.videoEnabled || false,
      chatMode: chatMode || storedOptions.chatMode || 'text',
      displayName:
        storedOptions.userDisplayName ||
        storedOptions.displayName ||
        storedOptions.username,
      firstName:
        storedOptions.userFirstName ||
        storedOptions.firstName ||
        storedOptions.username,
      browser,
      userLocale: storedOptions.userLocale || 'en',
      userLanguage: storedOptions.userLanguage || 'en',
      conversationId: conversationId || storedOptions.conversationId || null,
      username: storedOptions.username,
      conversationStarter: storedOptions.conversationStarter || null,
      isMobile: storedOptions.isMobile || false,
      userCompany: storedOptions.userCompany || '',
      userCompanyId: storedOptions.userCompanyId || null,
      partnerCandidate: storedOptions.partnerCandidate || false,
      avatar:
        storedOptions.avatar ||
        'https://www-yns-com.s3.amazonaws.com/images/icon_coach_generic.png',
      os: storedOptions.os || '',
      deloitteUser: storedOptions.deloitteUser || false,
      userCareerDevelopment: storedOptions.userCareerDevelopment || false,
      outplacement: storedOptions.outplacement || false,
      opRedesign: storedOptions.opRedesign || false,
      userId: storedOptions.userId || null,
    }

    Object.entries(fields).forEach(([fieldName, fieldValue]) => {
      if (fieldValue !== null && fieldValue !== undefined) {
        this.easyrtc.setRoomApiField(
          this.connectionState.chatRoom,
          fieldName,
          fieldValue,
        )
      }
    })
  }

  /**
   * Updates candidate presence when starting a chat
   *
   * Sets the candidate's presence to indicate they are starting a chat session.
   *
   * @param {string} conversationId - The conversation ID for this chat
   */
  updateCandidatePresenceStartingChat(conversationId) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }
    this.easyrtc.updatePresence('chat', 'Starting Chat', conversationId)

    this.connectionState.currentPresence = {
      state: 'chat',
      statusText: 'Starting Chat',
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Begin modal/selection → frame presence handoff.
   * Sets Starting Chat + startingChatAt without leaving the room.
   *
   * @param {string} [conversationId]
   */
  beginCandidateChatHandoff(conversationId) {
    const startingChatAt = Date.now()
    storeChatHandoffStartingAt(startingChatAt)

    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    this.easyrtc.setRoomApiField(
      this.connectionState.chatRoom,
      'startingChatAt',
      startingChatAt,
    )
    this.easyrtc.setRoomApiField(
      this.connectionState.chatRoom,
      'chatFrameJoined',
      false,
    )
    this.updateCandidatePresenceStartingChat(conversationId)
  }

  /**
   * Frame owns presence once connected to the room (not when coach answers).
   * Clears abandoned-load TTL markers; presence itself was already set on join.
   *
   * @param {string} [conversationId]
   */
  completeCandidateChatHandoff(_conversationId) {
    clearChatHandoffStartingAt()

    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    // Clear handoff TTL clock; frame join disarms the 30s abandoned-load filter
    this.easyrtc.setRoomApiField(
      this.connectionState.chatRoom,
      'startingChatAt',
      null,
    )
    this.easyrtc.setRoomApiField(
      this.connectionState.chatRoom,
      'chatFrameJoined',
      true,
    )
  }

  /**
   * Updates candidate presence when in active video chat
   *
   * Sets the candidate's presence to "do not disturb" with status indicating
   * they are in a video chat with the specified coach.
   *
   * @param {string} coachName - Name of the coach in the chat
   * @param {string} conversationId - The conversation ID for this chat
   */
  updateCandidatePresenceInVideoChat(coachName, conversationId) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const statusText = `In video chat with ${coachName}`
    this.easyrtc.updatePresence('dnd', statusText, conversationId)

    this.connectionState.currentPresence = {
      state: 'dnd',
      statusText: statusText,
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Updates candidate presence when in active audio chat
   *
   * Sets the candidate's presence to "do not disturb" with status indicating
   * they are in an audio chat with the specified coach.
   *
   * @param {string} coachName - Name of the coach in the chat
   * @param {string} conversationId - The conversation ID for this chat
   */
  updateCandidatePresenceInAudioChat(coachName, conversationId) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const statusText = `In audio chat with ${coachName}`
    this.easyrtc.updatePresence('dnd', statusText, conversationId)

    this.connectionState.currentPresence = {
      state: 'dnd',
      statusText: statusText,
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Updates candidate presence when in active text chat
   *
   * Sets the candidate's presence to "chat" with status indicating they are
   * in a text chat with the specified coach.
   *
   * @param {string} coachName - Name of the coach in the chat
   * @param {string} conversationId - The conversation ID for this chat
   */
  updateCandidatePresenceInTextChat(coachName, conversationId) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const statusText = `In text chat with ${coachName}`
    this.easyrtc.updatePresence('chat', statusText, conversationId)

    this.connectionState.currentPresence = {
      state: 'chat',
      statusText: statusText,
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Updates candidate presence when receiving a call
   *
   * Sets the candidate's presence to "do not disturb" with status indicating
   * they are currently receiving a call.
   */
  updateCandidatePresenceReceivingCall() {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }
    this.easyrtc.updatePresence('dnd', 'Receiving a call.')

    this.connectionState.currentPresence = {
      state: 'dnd',
      statusText: 'Receiving a call.',
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Updates candidate presence when video chat has ended
   *
   * Sets the candidate's presence to "away" with status indicating the video
   * chat with the specified coach has ended.
   *
   * @param {string} coachName - Name of the coach the chat was with
   * @param {string} conversationId - The conversation ID for the ended chat
   */
  updateCandidatePresenceVideoChatEnded(coachName, conversationId) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const statusText = `Video chat with ${coachName} ended`
    this.easyrtc.updatePresence('away', statusText, conversationId)

    this.connectionState.currentPresence = {
      state: 'away',
      statusText: statusText,
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Updates candidate presence when audio chat has ended
   *
   * Sets the candidate's presence to "away" with status indicating the audio
   * chat with the specified coach has ended.
   *
   * @param {string} coachName - Name of the coach the chat was with
   * @param {string} conversationId - The conversation ID for the ended chat
   */
  updateCandidatePresenceAudioChatEnded(coachName, conversationId) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const statusText = `Audio chat with ${coachName} ended`
    this.easyrtc.updatePresence('away', statusText, conversationId)

    this.connectionState.currentPresence = {
      state: 'away',
      statusText: statusText,
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Updates candidate presence when text chat has ended
   *
   * Sets the candidate's presence to "away" with status indicating the text
   * chat with the specified coach has ended.
   *
   * @param {string} coachName - Name of the coach the chat was with
   * @param {string} conversationId - The conversation ID for the ended chat
   */
  updateCandidatePresenceTextChatEnded(coachName, conversationId) {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    const statusText = `Text chat with ${coachName} ended`
    this.easyrtc.updatePresence('away', statusText, conversationId)

    this.connectionState.currentPresence = {
      state: 'away',
      statusText: statusText,
    }

    this.notifyListeners(
      'presenceUpdated',
      this.connectionState.currentPresence,
    )
  }

  /**
   * Updates candidate presence after chat has ended (legacy method)
   *
   * Sets the candidate's presence to "do not disturb" with "Chat Ended" status.
   * Uses 'dnd' state instead of 'away' to avoid filtering in legacy chat.js code.
   * This is a backward compatibility method.
   */
  updateCandidatePresenceAfterChatEnd() {
    if (!this.easyrtc || !this.connectionState.chatRoom) {
      return
    }

    // Set presence to indicate chat has ended
    // Use 'dnd' state for chat ended to avoid filtering in legacy chat.js
    // Legacy chat.js filters out candidates with presence.show = "away"
    this.easyrtc.updatePresence('dnd', 'Chat Ended')

    // Update local state
    this.connectionState.currentPresence = {
      state: 'dnd',
      statusText: 'Chat Ended',
    }

    // Notify listeners about presence change
    this.notifyListeners('presenceUpdated', {
      state: 'dnd',
      statusText: 'Chat Ended',
    })
  }
}

export default RtcService
